import { useEffect, useState, useCallback } from 'react';
import Navbar from '../components/common/Navbar';
import HighlightedSentence from '../components/common/HighlightedSentence';
import { useAuth } from '../context/AuthContext';

const PRODIGY_URL = import.meta.env.VITE_PRODIGY_URL || (import.meta.env.DEV ? 'http://localhost:8085' : '/prodigy');
const REFRESH_INTERVAL = 60;

const TYPE_META = {
  pos:       { label: 'POS Tagging',  color: '#2563eb', barColor: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-700'   },
  features:  { label: 'Morfología',   color: '#059669', barColor: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  relations: { label: 'Relaciones',   color: '#d97706', barColor: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700'  },
  ner:       { label: 'NER',          color: '#7c3aed', barColor: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700' },
};

const ProgressBar = ({ pct, barColor }) => (
  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
    <div
      className={`h-2 rounded-full transition-all duration-500 ${barColor || 'bg-blue-500'}`}
      style={{ width: `${pct}%` }}
    />
  </div>
);

const fmt = (n) => (n ?? 0).toLocaleString('es-PY');

const typeMeta = (atype) => TYPE_META[atype] || { label: atype, color: '#64748b', barColor: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' };

const TypeCoverageCard = ({ atype, info }) => {
  const meta = typeMeta(atype);
  const perSentence = info.sentences ? (info.annotations / info.sentences).toFixed(1) : '0';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <span className="font-semibold uppercase tracking-wide text-sm" style={{ color: meta.color }}>
          {meta.label}
        </span>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${meta.badge}`}>
          {info.annotators} anotadores
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { num: fmt(info.sentences), label: 'Oraciones' },
          { num: fmt(info.tagged_words), label: 'Palabras etiq.' },
          { num: perSentence, label: 'Anot. / oración' },
        ].map(({ num, label }) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
            <div className="text-xl font-bold" style={{ color: meta.color }}>{num}</div>
            <div className="text-xs text-gray-400 uppercase mt-1">{label}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1"><ProgressBar pct={info.word_coverage_pct} barColor={meta.barColor} /></div>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {fmt(info.tagged_words)}/{fmt(info.words_in_sentences)} palabras · {info.word_coverage_pct}%
        </span>
      </div>
    </div>
  );
};

const ReportPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);

  const { user } = useAuth();
  const isAdmin = user?.is_superuser === true;

  // Audit tab state (solo admin)
  const [annotators, setAnnotators] = useState([]);
  const [auditUser, setAuditUser] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  // Explorer tab state
  const [exFilters, setExFilters] = useState({ atype: '', group: '', batch: '', username: '', dateFrom: '', dateTo: '' });
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);

  // Daily tab state
  const [activeTab, setActiveTab] = useState('progress');
  const [dailyDateFrom, setDailyDateFrom] = useState(null);
  const [dailyDateTo, setDailyDateTo] = useState(null);
  const [dailyType, setDailyType] = useState('');
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  // IAA tab state
  const [iaaData, setIaaData] = useState(null);
  const [iaaLoading, setIaaLoading] = useState(false);
  const [iaaError, setIaaError] = useState(null);
  const [iaaType, setIaaType] = useState('pos');
  const [iaaBatch, setIaaBatch] = useState('');
  const [iaaExpandedPair, setIaaExpandedPair] = useState(null);

  // Conflicts state
  const [conflictsData, setConflictsData] = useState(null);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [conflictsError, setConflictsError] = useState(null);
  const [conflictsThreshold, setConflictsThreshold] = useState(0.3);
  const [conflictsFilterA, setConflictsFilterA] = useState('');
  const [conflictsFilterB, setConflictsFilterB] = useState('');
  const [conflictsExpanded, setConflictsExpanded] = useState(null);

  const fetchReport = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${PRODIGY_URL}/api/coverage-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(`No se pudo cargar el reporte: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnnotators = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${PRODIGY_URL}/api/annotators`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAnnotators(json.annotators || []);
    } catch {
      setAnnotators([]);
    }
  }, []);

  const fetchMatrix = useCallback(async (f) => {
    const token = localStorage.getItem('access_token');
    setMatrixLoading(true);
    setMatrixError(null);
    try {
      const p = new URLSearchParams();
      if (f.atype)    p.set('atype', f.atype);
      if (f.group)    p.set('group', f.group);
      if (f.batch)    p.set('batch', f.batch);
      if (f.username) p.set('username', f.username);
      if (f.dateFrom) p.set('date_from', f.dateFrom);
      if (f.dateTo)   p.set('date_to', f.dateTo);
      const qs = p.toString();
      const res = await fetch(`${PRODIGY_URL}/api/report-matrix${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMatrixData(await res.json());
    } catch (e) {
      setMatrixError(`No se pudo cargar: ${e.message}`);
    } finally {
      setMatrixLoading(false);
    }
  }, []);

  const fetchUserAudit = useCallback(async (username) => {
    if (!username) { setAuditData(null); return; }
    const token = localStorage.getItem('access_token');
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await fetch(`${PRODIGY_URL}/api/user-audit?username=${encodeURIComponent(username)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAuditData(json);
    } catch (e) {
      setAuditError(`No se pudo cargar la auditoría: ${e.message}`);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const fetchDailyReport = useCallback(async (dateFrom, dateTo, atype) => {
    const token = localStorage.getItem('access_token');
    setDailyLoading(true);
    setDailyError(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to',   dateTo);
      if (atype)    params.set('atype',     atype);
      const query = params.toString();
      const url = `${PRODIGY_URL}/api/daily-report${query ? `?${query}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setDailyData(json);
      if (json.error) setDailyError(json.error);
    } catch (e) {
      setDailyError(`No se pudo cargar: ${e.message}`);
    } finally {
      setDailyLoading(false);
    }
  }, []);

  const fetchIaaReport = useCallback(async (type, batch) => {
    const token = localStorage.getItem('access_token');
    setIaaLoading(true);
    setIaaError(null);
    setIaaData(null);
    setIaaExpandedPair(null);
    try {
      const params = new URLSearchParams({ annotation_type: type });
      if (batch) params.set('batch', batch);
      const res = await fetch(`${PRODIGY_URL}/api/iaa-report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.error) {
        setIaaError(json.error);
        setIaaData(null);
      } else {
        setIaaData(json);
      }
    } catch (e) {
      setIaaError(`No se pudo calcular IAA: ${e.message}`);
    } finally {
      setIaaLoading(false);
    }
  }, []);

  const fetchConflicts = useCallback(async (type, batch, threshold, filterA, filterB) => {
    const token = localStorage.getItem('access_token');
    setConflictsLoading(true);
    setConflictsError(null);
    setConflictsData(null);
    setConflictsExpanded(null);
    try {
      const params = new URLSearchParams({ annotation_type: type, threshold: String(threshold) });
      if (batch) params.set('batch', batch);
      if (filterA) params.set('annotator_a', filterA);
      if (filterB) params.set('annotator_b', filterB);
      const res = await fetch(`${PRODIGY_URL}/api/iaa-conflicts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.error) {
        setConflictsError(json.error);
      } else {
        setConflictsData(json);
      }
    } catch (e) {
      setConflictsError(`No se pudieron cargar conflictos: ${e.message}`);
    } finally {
      setConflictsLoading(false);
    }
  }, []);

  // Progress tab: carga inicial
  useEffect(() => {
    fetchReport();
    setCountdown(REFRESH_INTERVAL);
  }, [fetchReport]);

  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { fetchReport(); return REFRESH_INTERVAL; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [fetchReport]);

  // Audit/Explorer tabs: cargar lista de anotadores al abrir
  useEffect(() => {
    if (activeTab === 'audit' || activeTab === 'explorer') fetchAnnotators();
  }, [activeTab, fetchAnnotators]);

  // Explorer tab: carga inicial al abrir
  useEffect(() => {
    if (activeTab === 'explorer') fetchMatrix(exFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchMatrix]);

  // Daily tab: load when tab opens or date filter changes
  useEffect(() => {
    if (activeTab === 'daily') fetchDailyReport(dailyDateFrom, dailyDateTo, dailyType);
  }, [activeTab, dailyDateFrom, dailyDateTo, dailyType, fetchDailyReport]);

  // IAA tab: load when tab opens or type/batch changes
  useEffect(() => {
    if (activeTab === 'iaa') fetchIaaReport(iaaType, iaaBatch);
  }, [activeTab, iaaType, iaaBatch, fetchIaaReport]);

  const handleRefresh = () => { setLoading(true); setCountdown(REFRESH_INTERVAL); fetchReport(); };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reporte de Anotaciones</h1>
            <p className="text-gray-500 text-sm mt-1">Progreso y actividad del equipo de anotadores</p>
          </div>
          {activeTab === 'progress' && (
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">Actualiza en {countdown}s</span>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition"
              >
                {loading ? 'Cargando...' : '↻ Actualizar'}
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-200 rounded-lg p-1 mb-6 w-fit">
          {[
            { id: 'progress', label: 'Progreso General' },
            { id: 'explorer', label: 'Explorador' },
            { id: 'daily',    label: 'Actividad Diaria' },
            { id: 'iaa',      label: 'Acuerdo IAA' },
            ...(isAdmin ? [{ id: 'audit', label: 'Por Anotador' }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: PROGRESO GENERAL ── */}
        {activeTab === 'progress' && (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{error}</div>
            )}
            {loading && !data && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[1,2,3,4].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-64 shadow-sm" />
                ))}
              </div>
            )}
            {data?.overall && <CoverageView data={data} />}
          </>
        )}

        {/* ── TAB: EXPLORADOR ── */}
        {activeTab === 'explorer' && (
          <ExplorerView
            filters={exFilters}
            setFilters={setExFilters}
            annotators={annotators}
            data={matrixData}
            loading={matrixLoading}
            error={matrixError}
            onApply={() => fetchMatrix(exFilters)}
            quota={matrixData?.quota || 50}
          />
        )}

        {/* ── TAB: ACTIVIDAD DIARIA ── */}
        {activeTab === 'daily' && (
          <>
            {/* Date range filter + refresh */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <label className="text-sm text-gray-600 font-medium">Desde:</label>
              <input
                type="date"
                max={dailyDateTo || today}
                value={dailyDateFrom || ''}
                onChange={(e) => setDailyDateFrom(e.target.value || null)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label className="text-sm text-gray-600 font-medium">Hasta:</label>
              <input
                type="date"
                min={dailyDateFrom || undefined}
                max={today}
                value={dailyDateTo || ''}
                onChange={(e) => setDailyDateTo(e.target.value || null)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label className="text-sm text-gray-600 font-medium">Tipo:</label>
              <select
                value={dailyType}
                onChange={(e) => setDailyType(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="pos">POS</option>
                <option value="features">Features</option>
                <option value="ner">NER</option>
                <option value="relations">Relations</option>
              </select>
              {(dailyDateFrom || dailyDateTo || dailyType) && (
                <button
                  onClick={() => { setDailyDateFrom(null); setDailyDateTo(null); setDailyType(''); }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Limpiar filtro
                </button>
              )}
              <button
                onClick={() => fetchDailyReport(dailyDateFrom, dailyDateTo, dailyType)}
                disabled={dailyLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition"
              >
                {dailyLoading ? 'Cargando...' : '↻ Actualizar'}
              </button>
              {dailyData && !dailyError && (
                <span className="text-sm text-gray-500">
                  {dailyData.total_annotations} anotaciones · {dailyData.total_users} anotadores
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-6">
              El filtro usa la hora exacta por oración cuando está disponible; para anotaciones antiguas sin timestamp se usa la fecha del dataset como aproximación.
            </p>

            {dailyError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{dailyError}</div>
            )}

            {dailyLoading && !dailyData && (
              <div className="space-y-3">
                {[1,2,3].map((i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-20 shadow-sm" />)}
              </div>
            )}

            {dailyData && !dailyLoading && Object.keys(dailyData.by_user).length === 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
                <div className="text-4xl mb-3">📭</div>
                <p className="text-gray-500 text-sm">{(dailyDateFrom || dailyDateTo) ? `Sin actividad registrada para el rango seleccionado` : 'Sin datos de anotación disponibles'}</p>
              </div>
            )}

            {dailyData && Object.entries(dailyData.by_user).map(([username, udata]) => (
              <DailyUserCard
                key={username}
                username={username}
                udata={udata}
                expanded={expandedUser === username}
                onToggle={() => setExpandedUser(expandedUser === username ? null : username)}
              />
            ))}
          </>
        )}

        {/* ── TAB: ACUERDO IAA ── */}
        {activeTab === 'iaa' && (
          <>
            {/* Type selector + batch + refresh */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <label className="text-sm text-gray-600 font-medium">Tipo de anotación:</label>
              <div className="flex gap-1 bg-gray-200 rounded-lg p-1">
                {Object.entries(TYPE_META).map(([type, meta]) => (
                  <button
                    key={type}
                    onClick={() => setIaaType(type)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                      iaaType === type ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {meta.label}
                  </button>
                ))}
              </div>
              <label className="text-sm text-gray-600 font-medium">Batch:</label>
              <input
                type="number"
                min="1"
                placeholder="último"
                title="Vacío = último batch de este tipo con al menos 2 anotadores"
                value={iaaBatch}
                onChange={e => setIaaBatch(e.target.value)}
                className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={() => fetchIaaReport(iaaType, iaaBatch)}
                disabled={iaaLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition"
              >
                {iaaLoading ? 'Calculando...' : '↻ Recalcular'}
              </button>
              <p className="text-xs text-gray-400">
                El cálculo puede tardar ~10–30s dependiendo del tamaño del dataset.
              </p>
            </div>

            {iaaError && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mb-6 text-sm">{iaaError}</div>
            )}

            {iaaLoading && (
              <div className="space-y-3">
                {[1,2,3].map((i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-24 shadow-sm" />)}
              </div>
            )}

            {iaaData && !iaaLoading && (
              <>
                {/* Summary card */}
                <IaaSummaryCard summary={iaaData.summary} dataset={iaaData.dataset} />

                {/* Outlier warnings */}
                {iaaData.outliers && iaaData.outliers.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-red-600 font-semibold text-sm">⚠ Anotadores con bajo acuerdo (&lt; 0.50)</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {iaaData.outliers.map((name) => (
                        <span key={name} className="bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                          {name} · κ={iaaData.annotator_stats?.[name]?.avg_kappa ?? '—'}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-red-500 mt-2">Recomendación: sesión de calibración o revisión de guidelines.</p>
                  </div>
                )}

                {/* Pairs table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-5">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-800 text-sm">Acuerdo por par de anotadores</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Ordenado por Kappa descendente · click para ver por etiqueta</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {iaaData.pairs.map((pair, idx) => {
                      const key = `${pair.annotator_a}↔${pair.annotator_b}`;
                      const isExpanded = iaaExpandedPair === key;
                      return (
                        <div key={idx}>
                          <button
                            onClick={() => setIaaExpandedPair(isExpanded ? null : key)}
                            className="w-full flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition text-left"
                          >
                            <div className="flex-1 flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate">{pair.annotator_a}</span>
                              <span className="text-gray-400 text-xs">↔</span>
                              <span className="text-sm font-medium text-gray-900 truncate">{pair.annotator_b}</span>
                            </div>
                            <KappaBadge kappa={pair.kappa} />
                            <span className="text-xs text-gray-400 w-20 text-right">{pair.n_compared} oraciones</span>
                            <span className="text-gray-400 text-xs ml-1">{isExpanded ? '▲' : '▼'}</span>
                          </button>
                          {isExpanded && pair.label_agreements && Object.keys(pair.label_agreements).length > 0 && (
                            <div className="px-5 pb-4 bg-gray-50 border-t border-gray-100">
                              <p className="text-xs text-gray-500 font-medium mb-2 pt-3">Acuerdo por etiqueta:</p>
                              <div className="flex flex-wrap gap-1.5">
                                {Object.entries(pair.label_agreements).map(([label, agr]) => (
                                  <span
                                    key={label}
                                    className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                                      agr >= 0.8 ? 'bg-green-50 text-green-700 border-green-200' :
                                      agr >= 0.5 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                      'bg-red-50 text-red-700 border-red-200'
                                    }`}
                                  >
                                    {label}: {(agr * 100).toFixed(0)}%
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Annotator avg kappa */}
                {iaaData.annotator_stats && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-800 text-sm">Kappa promedio por anotador</h3>
                    </div>
                    <div className="px-5 py-4 flex flex-wrap gap-3">
                      {Object.entries(iaaData.annotator_stats)
                        .sort((a, b) => b[1].avg_kappa - a[1].avg_kappa)
                        .map(([name, stats]) => (
                          <div key={name} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                            stats.is_outlier ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <span className={`font-medium ${stats.is_outlier ? 'text-red-700' : 'text-gray-800'}`}>{name}</span>
                            <KappaBadge kappa={stats.avg_kappa} small />
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* ── Conflict Viewer ── */}
                <ConflictViewer
                  iaaType={iaaType}
                  iaaBatch={iaaBatch}
                  annotatorList={iaaData.annotator_stats ? Object.keys(iaaData.annotator_stats) : []}
                  threshold={conflictsThreshold}
                  setThreshold={setConflictsThreshold}
                  filterA={conflictsFilterA}
                  setFilterA={setConflictsFilterA}
                  filterB={conflictsFilterB}
                  setFilterB={setConflictsFilterB}
                  data={conflictsData}
                  loading={conflictsLoading}
                  error={conflictsError}
                  expanded={conflictsExpanded}
                  setExpanded={setConflictsExpanded}
                  onFetch={() => fetchConflicts(iaaType, iaaBatch, conflictsThreshold, conflictsFilterA, conflictsFilterB)}
                />
              </>
            )}
          </>
        )}

        {/* ── TAB: POR ANOTADOR (admin) ── */}
        {activeTab === 'audit' && isAdmin && (
          <AuditView
            annotators={annotators}
            auditUser={auditUser}
            setAuditUser={setAuditUser}
            data={auditData}
            loading={auditLoading}
            error={auditError}
            onSelect={fetchUserAudit}
            quota={auditData?.quota || 50}
          />
        )}

      </div>
    </div>
  );
};

// ─── IAA Helper Components ────────────────────────────────────────────────

const KAPPA_COLOR = (k) => {
  if (k >= 0.80) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (k >= 0.60) return 'bg-green-100 text-green-700 border-green-200';
  if (k >= 0.40) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  if (k >= 0.20) return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const KappaBadge = ({ kappa, small }) => (
  <span className={`font-mono font-semibold border rounded-full ${small ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-0.5'} ${KAPPA_COLOR(kappa)}`}>
    κ={kappa.toFixed(3)}
  </span>
);

const IaaSummaryCard = ({ summary, dataset }) => {
  const kappa = summary.avg_kappa;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-gray-800">Resumen General</h3>
          <p className="text-xs text-gray-400 mt-0.5">Dataset: <code className="bg-gray-100 px-1 rounded">{dataset}</code></p>
        </div>
        <div className="flex items-center gap-2">
          <KappaBadge kappa={kappa} />
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${KAPPA_COLOR(kappa)}`}>
            {summary.kappa_interpretation}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Kappa promedio', value: kappa.toFixed(3) },
          { label: 'Acuerdo simple', value: `${(summary.avg_agreement * 100).toFixed(1)}%` },
          { label: 'Pares evaluados', value: summary.n_pairs },
          { label: 'Anotadores', value: summary.n_annotators },
        ].map(({ label, value }) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-center">
            <div className="text-xl font-bold text-gray-900">{value}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide mt-1">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Daily Activity Card ───────────────────────────────────────────────────

// ─── Conflict Viewer Component ─────────────────────────────────────────────

const CONFLICT_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', hex: '#3b82f6' },
  { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', hex: '#f59e0b' },
  { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200', hex: '#10b981' },
  { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200', hex: '#f43f5e' },
  { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-200', hex: '#8b5cf6' },
];

const DisagreementBadge = ({ score }) => {
  const pct = Math.round(score * 100);
  const color = score >= 0.7 ? 'bg-red-100 text-red-700 border-red-200'
               : score >= 0.5 ? 'bg-orange-100 text-orange-700 border-orange-200'
               : 'bg-yellow-100 text-yellow-700 border-yellow-200';
  return (
    <span className={`text-xs font-semibold border rounded-full px-2.5 py-0.5 ${color}`}>
      {pct}% desacuerdo
    </span>
  );
};

const AnnotatedText = ({ text, spans, color }) => {
  if (!text) return <span className="text-gray-400 italic">sin texto</span>;
  if (!spans || spans.length === 0) return <span className="text-gray-700">{text}</span>;

  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const parts = [];
  let cursor = 0;
  for (const span of sorted) {
    if (span.start > cursor) parts.push({ type: 'text', content: text.slice(cursor, span.start) });
    if (span.start < span.end) {
      parts.push({ type: 'span', content: text.slice(span.start, span.end), label: span.label });
    }
    cursor = span.end;
  }
  if (cursor < text.length) parts.push({ type: 'text', content: text.slice(cursor) });

  return (
    <span>
      {parts.map((p, i) =>
        p.type === 'span' ? (
          <mark
            key={i}
            title={p.label}
            className={`rounded px-0.5 mx-0.5 ${color.bg} ${color.text}`}
            style={{ outline: `1.5px solid ${color.hex}`, outlineOffset: '1px' }}
          >
            {p.content}
            <sup className={`text-[9px] font-bold ml-0.5 ${color.text}`}>{p.label}</sup>
          </mark>
        ) : (
          <span key={i}>{p.content}</span>
        )
      )}
    </span>
  );
};

const ConflictCard = ({ conflict, index, expanded, onToggle }) => {
  const { text, annotations, disagreement_score, meta } = conflict;
  const sessionColors = {};
  annotations.forEach((ann, i) => {
    sessionColors[ann.session_id] = CONFLICT_COLORS[i % CONFLICT_COLORS.length];
  });

  const shortName = (sid) => sid.split('-').pop() || sid;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs font-mono text-gray-400 shrink-0">#{index + 1}</span>
          <span className="text-sm text-gray-700 truncate">{text.slice(0, 90)}{text.length > 90 ? '…' : ''}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <DisagreementBadge score={disagreement_score} />
          <span className="text-xs text-gray-400">{conflict.n_annotators} anotadores</span>
          {conflict.n_empty > 0 && (
            <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5" title="Anotadores sin spans (excluidos del cálculo)">
              {conflict.n_empty} sin anotar
            </span>
          )}
          <span className="text-gray-400 text-lg">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-3">
          {meta && Object.keys(meta).length > 0 && (
            <div className="text-xs text-gray-400">
              {Object.entries(meta).map(([k, v]) => (
                <span key={k} className="mr-3"><strong>{k}:</strong> {v}</span>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {annotations.map((ann) => {
              const color = sessionColors[ann.session_id];
              return (
                <div key={ann.session_id} className={`rounded-lg border px-4 py-3 ${color.bg} ${color.border}`}>
                  <div className={`text-xs font-semibold mb-2 ${color.text} flex items-center gap-2`}>
                    {shortName(ann.session_id)}
                    {ann.spans.length === 0 ? (
                      <span className="text-gray-400 font-normal italic">(sin spans — excluido del cálculo)</span>
                    ) : (
                      <span className="font-normal text-gray-500">{ann.spans.length} spans</span>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed">
                    <AnnotatedText text={text} spans={ann.spans} color={color} />
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ConflictViewer = ({
  iaaType, iaaBatch, annotatorList, threshold, setThreshold,
  filterA, setFilterA, filterB, setFilterB,
  data, loading, error, expanded, setExpanded, onFetch,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-left"
      >
        <div>
          <h3 className="font-semibold text-gray-800">Ver Conflictos entre Anotadores</h3>
          <p className="text-xs text-gray-400 mt-0.5">Oraciones donde los anotadores discrepan</p>
        </div>
        <span className="text-gray-400 text-lg">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          {/* Filters row */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Umbral de desacuerdo</label>
              <div className="flex items-center gap-2">
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={threshold}
                  onChange={e => setThreshold(parseFloat(e.target.value))}
                  className="w-28 accent-blue-600"
                />
                <span className="text-sm font-mono text-gray-700 w-8">{Math.round(threshold * 100)}%</span>
              </div>
            </div>
            {annotatorList.length > 0 && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500">Anotador A (opcional)</label>
                  <select value={filterA} onChange={e => setFilterA(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Todos</option>
                    {annotatorList.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500">Anotador B (opcional)</label>
                  <select value={filterB} onChange={e => setFilterB(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                    <option value="">Todos</option>
                    {annotatorList.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </>
            )}
            <button
              onClick={onFetch}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? 'Cargando…' : 'Buscar Conflictos'}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
          )}

          {data && !loading && (
            <>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>
                  <strong className="text-gray-900">{data.total}</strong> conflicto{data.total !== 1 ? 's' : ''} encontrado{data.total !== 1 ? 's' : ''}
                  {' '}en <code className="bg-gray-100 rounded px-1">{data.dataset}</code>
                </span>
                {data.total === 0 && (
                  <span className="text-emerald-600 font-medium">✓ Buena concordancia con este umbral</span>
                )}
              </div>
              {data.conflicts.map((c, i) => (
                <ConflictCard
                  key={c.task_hash ?? i}
                  conflict={c}
                  index={i}
                  expanded={expanded === i}
                  onToggle={() => setExpanded(expanded === i ? null : i)}
                />
              ))}
            </>
          )}

          {!data && !loading && !error && (
            <p className="text-sm text-gray-400 text-center py-4">
              Ajustá los filtros y presioná "Buscar Conflictos"
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────


const ANSWER_BADGE = {
  accept:  'bg-green-100 text-green-700',
  reject:  'bg-red-100 text-red-700',
  ignore:  'bg-gray-100 text-gray-500',
};

const DailyUserCard = ({ username, udata, expanded, onToggle }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-3 overflow-hidden">
      {/* Header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex items-center justify-center uppercase">
            {username[0]}
          </div>
          <div>
            <div className="font-semibold text-gray-900">{username}</div>
            <div className="flex gap-2 mt-1">
              {Object.entries(udata.by_type).map(([t, cnt]) => {
                const meta = TYPE_META[t] || { label: t, badge: 'bg-gray-100 text-gray-600' };
                return (
                  <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>
                    {meta.label}: {cnt}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-lg font-bold text-gray-900">{udata.total}</div>
            <div className="text-xs text-gray-400">oraciones</div>
          </div>
          {/* Promedio de duración si hay timestamps precisos */}
          {udata.has_precise_timestamps && (() => {
            const durs = udata.annotations.filter(a => a.duration_ms != null).map(a => a.duration_ms);
            if (durs.length === 0) return null;
            const avg = durs.reduce((s, d) => s + d, 0) / durs.length;
            return (
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-emerald-600">
                  {avg < 60000 ? `${(avg / 1000).toFixed(1)}s` : `${Math.floor(avg / 60000)}m${Math.round((avg % 60000) / 1000)}s`}
                </div>
                <div className="text-xs text-gray-400">prom/oración</div>
              </div>
            );
          })()}
          <span className="text-gray-400 text-lg">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {/* Expanded annotation list */}
      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-50 max-h-[480px] overflow-y-auto">
          {/* Header indicador de precisión */}
          {udata.annotations.length > 0 && (
            <div className="px-5 py-2 bg-gray-50 flex items-center gap-2">
              {udata.has_precise_timestamps ? (
                <span className="text-xs text-green-600 font-medium">Timestamps precisos disponibles</span>
              ) : (
                <span className="text-xs text-amber-500">Fecha estimada (nivel de sesión) — timestamps precisos disponibles en nuevas anotaciones</span>
              )}
            </div>
          )}
          {udata.annotations.map((ann, idx) => (
            <div key={idx} className="px-5 py-3 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-3 mb-1">
                <p className="text-sm text-gray-800 flex-1 leading-relaxed">
                  <HighlightedSentence text={ann.text} marks={ann.summary?.marks} atype={ann.type} />
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ANSWER_BADGE[ann.answer] || 'bg-gray-100 text-gray-500'}`}>
                    {ann.answer}
                  </span>
                  {ann.batch && (
                    <span className="text-xs text-gray-400">B{String(ann.batch).padStart(2,'0')}</span>
                  )}
                  {/* Timestamp: preciso (hora:min:seg) o estimado (solo fecha) */}
                  {ann.annotated_at && (
                    <span className={`text-xs ${ann.has_precise_ts ? 'text-blue-400 font-mono' : 'text-gray-300'}`} title={ann.has_precise_ts ? 'Hora exacta de anotación' : 'Fecha estimada (inicio de sesión)'}>
                      {ann.annotated_at}
                    </span>
                  )}
                  {/* Duración por oración */}
                  {ann.duration_ms != null && (
                    <span className="text-xs text-emerald-500 font-mono" title="Tiempo en anotar esta oración">
                      {ann.duration_ms < 60000
                        ? `${(ann.duration_ms / 1000).toFixed(1)}s`
                        : `${Math.floor(ann.duration_ms / 60000)}m${Math.round((ann.duration_ms % 60000) / 1000)}s`}
                    </span>
                  )}
                </div>
              </div>

              {/* Annotation details */}
              {ann.summary && (() => {
                const tagged  = ann.summary.tagged_tokens || [];
                const rels    = ann.summary.relations     || [];
                const TYPE_BADGE = {
                  pos:      'bg-blue-50 text-blue-700 border-blue-200',
                  features: 'bg-green-50 text-green-700 border-green-200',
                  ner:      'bg-violet-50 text-violet-700 border-violet-200',
                  relations:'bg-amber-50 text-amber-700 border-amber-200',
                };
                const badgeCls = TYPE_BADGE[ann.type] || 'bg-gray-50 text-gray-600 border-gray-200';
                return (
                  <div className="mt-2 space-y-1">
                    {/* POS / Features / NER: tokens con su etiqueta */}
                    {tagged.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tagged.map((t, i) => (
                          <span key={i} className={`inline-flex items-center gap-1 text-xs border px-2 py-0.5 rounded-full ${badgeCls}`}>
                            <span className="font-medium">{t.text}</span>
                            <span className="opacity-60 text-[10px] uppercase tracking-wide">{t.tag}</span>
                          </span>
                        ))}
                        {(ann.summary.span_count > tagged.length) && (
                          <span className="text-xs text-gray-400 self-center">+{ann.summary.span_count - tagged.length} más</span>
                        )}
                      </div>
                    )}
                    {/* Sin etiquetas */}
                    {tagged.length === 0 && rels.length === 0 && ann.answer !== 'ignore' && (
                      <p className="text-xs text-gray-400 italic">Sin etiquetas asignadas</p>
                    )}
                    {/* Relations */}
                    {rels.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rels.map((r, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
                            <span className="font-medium">{r.head}</span>
                            <span className="text-amber-400">→</span>
                            <span className="font-semibold text-[10px] uppercase tracking-wide">{r.label}</span>
                            <span className="text-amber-400">→</span>
                            <span className="font-medium">{r.child}</span>
                          </span>
                        ))}
                        {(ann.summary.relation_count > rels.length) && (
                          <span className="text-xs text-gray-400 self-center">+{ann.summary.relation_count - rels.length} más</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Explorer View (filtros + matriz + por grupo + CSV) ────────────────────

const HEAT = (pct) => {
  if (pct <= 0)   return 'bg-gray-100 text-gray-400';
  if (pct >= 100) return 'bg-emerald-500 text-white';
  if (pct >= 60)  return 'bg-emerald-200 text-emerald-900';
  if (pct >= 30)  return 'bg-amber-200 text-amber-900';
  return 'bg-orange-200 text-orange-900';
};

const csvEscape = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const ExplorerView = ({ filters, setFilters, annotators, data, loading, error, onApply, quota }) => {
  const groups = data?.groups || [];
  const cells = data?.cells || [];
  const batchesByType = data?.batches_by_type || {};

  // Opciones de batch (según tipo elegido, o unión de todos)
  const batchOptions = filters.atype
    ? (batchesByType[filters.atype] || [])
    : Array.from(new Set(Object.values(batchesByType).flat())).sort((a, b) => a - b);

  // Matriz Anotador × Batch (suma de conteos por celda)
  const rows = Array.from(new Set(cells.map((c) => c.annotator))).sort();
  const cols = Array.from(new Set(cells.map((c) => c.batch))).sort((a, b) => a - b);
  const lookup = {};
  cells.forEach((c) => {
    const k = `${c.annotator}|${c.batch}`;
    lookup[k] = (lookup[k] || 0) + c.count;
  });

  // Reporte por grupo (según filtros activos)
  const groupRows = groups
    .filter((g) => !filters.group || g.svc_id === filters.group)
    .filter((g) => !filters.atype || g.annotation_type === filters.atype)
    .map((g) => {
      const gcells = cells.filter((c) => c.group_svc_id === g.svc_id);
      const done = gcells.reduce((s, c) => s + c.count, 0);
      const completeCells = gcells.filter((c) => c.complete).length;
      const nb = filters.batch ? 1 : Math.max(1, (g.end_batch - g.start_batch + 1));
      const expectedCells = Math.max(1, g.members.length * nb);
      const pct = Math.min(100, Math.round((done / (expectedCells * quota)) * 100));
      return { g, done, completeCells, expectedCells, pct };
    });

  const exportCsv = () => {
    const header = ['Tipo', 'Grupo', 'Batch', 'Anotador', 'Anotadas', 'Cuota', 'Completo'];
    const lines = [header.join(',')];
    cells
      .slice()
      .sort((a, b) => a.annotator.localeCompare(b.annotator) || a.batch - b.batch)
      .forEach((c) => lines.push([
        c.annotation_type, c.group_name || '', c.batch, c.annotator, c.count, quota, c.complete ? 'si' : 'no',
      ].map(csvEscape).join(',')));
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_anotaciones_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearAll = () => setFilters({ atype: '', group: '', batch: '', username: '', dateFrom: '', dateTo: '' });
  const selCls = 'border border-gray-300 rounded-lg px-2.5 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <>
      {/* ── Barra de filtros ── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Tipo</label>
            <select value={filters.atype} onChange={(e) => set('atype', e.target.value)} className={selCls}>
              <option value="">Todos</option>
              {Object.entries(TYPE_META).map(([t, m]) => <option key={t} value={t}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Grupo</label>
            <select value={filters.group} onChange={(e) => set('group', e.target.value)} className={selCls}>
              <option value="">Todos</option>
              {groups.map((g) => <option key={g.svc_id} value={g.svc_id}>{g.name || g.svc_id}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Batch</label>
            <select value={filters.batch} onChange={(e) => set('batch', e.target.value)} className={selCls}>
              <option value="">Todos</option>
              {batchOptions.map((b) => <option key={b} value={b}>Batch {String(b).padStart(2, '0')}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Anotador</label>
            <select value={filters.username} onChange={(e) => set('username', e.target.value)} className={selCls}>
              <option value="">Todos</option>
              {annotators.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Desde</label>
            <input type="date" value={filters.dateFrom} max={filters.dateTo || undefined} onChange={(e) => set('dateFrom', e.target.value)} className={selCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Hasta</label>
            <input type="date" value={filters.dateTo} min={filters.dateFrom || undefined} onChange={(e) => set('dateTo', e.target.value)} className={selCls} />
          </div>
          <button onClick={onApply} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition">
            {loading ? 'Cargando...' : 'Aplicar'}
          </button>
          <button onClick={clearAll} className="text-sm text-gray-500 hover:text-gray-700 underline px-1">Limpiar</button>
          <button onClick={exportCsv} disabled={cells.length === 0} className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-40 transition">
            ↓ Exportar CSV
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-5 text-sm">{error}</div>}
      {loading && <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-40 shadow-sm mb-5" />}

      {data && !loading && cells.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
          <div className="text-4xl mb-3">🔎</div>
          <p className="text-gray-500 text-sm">Sin datos para los filtros seleccionados.</p>
        </div>
      )}

      {data && !loading && cells.length > 0 && (
        <>
          {/* ── Matriz Anotador × Batch ── */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 text-sm">Matriz Anotador × Batch</h3>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> completo</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 inline-block" /> parcial</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 inline-block" /> vacío</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="text-sm border-collapse w-full">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-gray-50 text-left text-xs font-semibold text-gray-500 px-3 py-2 border-b border-gray-100">Anotador</th>
                    {cols.map((b) => (
                      <th key={b} className="text-xs font-semibold text-gray-500 px-2 py-2 border-b border-gray-100 text-center whitespace-nowrap">B{String(b).padStart(2, '0')}</th>
                    ))}
                    <th className="text-xs font-semibold text-gray-500 px-3 py-2 border-b border-gray-100 text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((ann) => {
                    const rowTotal = cols.reduce((s, b) => s + (lookup[`${ann}|${b}`] || 0), 0);
                    return (
                      <tr key={ann} className="hover:bg-gray-50">
                        <td className="sticky left-0 bg-white text-gray-700 px-3 py-1.5 truncate max-w-[160px] border-b border-gray-50" title={ann}>{ann}</td>
                        {cols.map((b) => {
                          const v = lookup[`${ann}|${b}`] || 0;
                          const pct = Math.min(100, Math.round((v / quota) * 100));
                          return (
                            <td key={b} className="px-1 py-1 text-center border-b border-gray-50">
                              <span className={`inline-block w-12 rounded py-1 text-xs font-medium ${HEAT(pct)}`} title={`${v}/${quota}`}>{v}</span>
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5 text-center font-semibold text-gray-900 border-b border-gray-50">{rowTotal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-2 text-xs text-gray-400">{rows.length} anotadores · {cols.length} batches · cuota {quota}</div>
          </div>

          {/* ── Reporte por Grupo ── */}
          {groupRows.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {groupRows.map(({ g, done, completeCells, expectedCells, pct }) => {
                const meta = TYPE_META[g.annotation_type] || { label: g.annotation_type, color: '#64748b', barColor: 'bg-gray-400' };
                return (
                  <div key={g.svc_id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <span className="font-semibold text-gray-900">{g.name || g.svc_id}</span>
                        <span className="ml-2 text-xs font-medium uppercase" style={{ color: meta.color }}>{meta.label}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{pct}%</span>
                    </div>
                    <div className="mb-3"><ProgressBar pct={pct} barColor={meta.barColor} /></div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {[
                        { num: g.members.length, label: 'Miembros' },
                        { num: `${g.start_batch}–${g.end_batch}`, label: 'Batches' },
                        { num: done, label: 'Anotadas' },
                      ].map(({ num, label }) => (
                        <div key={label} className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                          <div className="text-lg font-bold text-gray-900">{num}</div>
                          <div className="text-[10px] text-gray-400 uppercase mt-0.5">{label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-400 mt-2 text-center">{completeCells}/{expectedCells} cuotas completas (según filtros)</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
};

// ─── Coverage View (Progreso General) ──────────────────────────────────────

const CoverageView = ({ data }) => {
  const { overall, by_type: byType, groups, by_annotator: byAnnotator } = data;
  const [annType, setAnnType] = useState('');
  const [groupType, setGroupType] = useState('');
  const groupRows = groups.filter((g) => !groupType || g.annotation_type === groupType);
  const corpusPct = overall.corpus_sentences
    ? Math.round((overall.sentences / overall.corpus_sentences) * 1000) / 10 : null;
  const annotatorRows = byAnnotator.filter((r) => !annType || r.annotation_type === annType);
  const thCls = 'text-left text-xs font-semibold text-gray-500 px-4 py-2 border-b border-gray-100';
  const tdCls = 'px-4 py-2 border-b border-gray-50';

  return (
    <>
      {/* KPIs globales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
        {[
          {
            num: fmt(overall.sentences),
            label: 'Oraciones únicas anotadas',
            sub: corpusPct != null ? `de ${fmt(overall.corpus_sentences)} en el corpus · ${corpusPct}%` : null,
          },
          {
            num: fmt(overall.tagged_words),
            label: 'Palabras con etiqueta',
            sub: `de ${fmt(overall.words_in_sentences)} palabras de esas oraciones · ${overall.word_coverage_pct}%`,
          },
          { num: fmt(overall.annotations), label: 'Anotaciones', sub: 'respuestas aceptadas con etiquetas' },
          { num: fmt(overall.annotators), label: 'Anotadores', sub: 'con al menos una anotación' },
        ].map(({ num, label, sub }) => (
          <div key={label} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">{label}</div>
            <div className="text-2xl font-bold text-gray-900">{num}</div>
            {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mb-6">
        Una oración cuenta si al menos un anotador le puso al menos una etiqueta; una palabra cuenta si al menos un
        anotador la etiquetó (en relaciones: si es origen o destino de una relación). La misma oración en varios
        batches o anotada por varias personas cuenta una vez. Solo respuestas aceptadas. Los totales globales unen todos los tipos.
      </p>

      {/* Por tipo */}
      {Object.keys(byType).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {Object.entries(byType).map(([atype, info]) => (
            <TypeCoverageCard key={atype} atype={atype} info={info} />
          ))}
        </div>
      )}

      {/* Por grupo */}
      {groups.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">Por grupo</h3>
              <p className="text-xs text-gray-400 mt-0.5">Oraciones únicas anotadas por los miembros del grupo dentro de su rango de batches</p>
            </div>
            <select
              value={groupType}
              onChange={(e) => setGroupType(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los tipos</option>
              {Array.from(new Set(groups.map((g) => g.annotation_type))).map((t) => (
                <option key={t} value={t}>{typeMeta(t).label}</option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="text-sm border-collapse w-full">
              <thead>
                <tr>
                  {['Grupo', 'Tipo', 'Batches', 'Oraciones', 'Palabras etiq.', 'Miembros'].map((h) => (
                    <th key={h} className={thCls}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupRows.map((g) => {
                  const meta = typeMeta(g.annotation_type);
                  const pct = g.range_sentences ? Math.min(100, Math.round((g.sentences / g.range_sentences) * 100)) : 0;
                  return (
                    <tr key={g.svc_id} className="hover:bg-gray-50 align-top">
                      <td className={`${tdCls} font-medium text-gray-900`}>{g.name}</td>
                      <td className={tdCls}><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>{meta.label}</span></td>
                      <td className={`${tdCls} text-gray-600 whitespace-nowrap`}>
                        {g.start_batch}–{g.end_batch}
                        <div className="text-xs text-gray-400" title={`Con anotaciones: ${(g.batches_with_data || []).join(', ') || 'ninguno'}\nInstancias de Prodigy encendidas: ${(g.running_batches || []).join(', ') || 'ninguna'}`}>
                          {(g.batches_with_data || []).length}/{g.total_batches} con datos · {(g.running_batches || []).length} activos
                        </div>
                      </td>
                      <td className={`${tdCls} min-w-[180px]`}>
                        <div className="flex items-center gap-2">
                          <div className="flex-1"><ProgressBar pct={pct} barColor={meta.barColor} /></div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{fmt(g.sentences)}/{fmt(g.range_sentences)}</span>
                        </div>
                      </td>
                      <td className={`${tdCls} text-gray-600 whitespace-nowrap`}>
                        {fmt(g.tagged_words)} <span className="text-xs text-gray-400">({g.word_coverage_pct}%)</span>
                      </td>
                      <td className={`${tdCls} text-xs text-gray-600`}>
                        {g.members.length === 0 ? <span className="text-gray-400">sin miembros</span> : g.members.map((m) => (
                          <span key={m} className="inline-block mr-2 whitespace-nowrap">
                            {m} <span className="text-gray-400">{g.per_member_sentences[m] ?? 0}</span>
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Por anotador */}
      {byAnnotator.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">Por anotador</h3>
              <p className="text-xs text-gray-400 mt-0.5">Histórico completo, todos los batches</p>
            </div>
            <select
              value={annType}
              onChange={(e) => setAnnType(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los tipos</option>
              {Object.keys(byType).map((t) => <option key={t} value={t}>{typeMeta(t).label}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="text-sm border-collapse w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  {['Anotador', 'Tipo', 'Oraciones', 'Palabras etiq.', '% palabras'].map((h) => (
                    <th key={h} className={thCls}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {annotatorRows.map((r) => {
                  const meta = typeMeta(r.annotation_type);
                  return (
                    <tr key={`${r.annotation_type}|${r.annotator}`} className="hover:bg-gray-50">
                      <td className={`${tdCls} text-gray-900`}>{r.annotator}</td>
                      <td className={tdCls}><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>{meta.label}</span></td>
                      <td className={`${tdCls} font-semibold text-gray-900`}>{fmt(r.sentences)}</td>
                      <td className={`${tdCls} text-gray-600`}>{fmt(r.tagged_words)} <span className="text-xs text-gray-400">/ {fmt(r.words_in_sentences)}</span></td>
                      <td className={`${tdCls} text-gray-600`}>{r.word_coverage_pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {Object.keys(byType).length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-gray-500 text-sm">Todavía no hay anotaciones con etiquetas.</p>
        </div>
      )}
    </>
  );
};

// ─── Audit View (Por Anotador) ─────────────────────────────────────────────

const AuditView = ({ annotators, auditUser, setAuditUser, data, loading, error, onSelect, quota }) => {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <label className="text-sm text-gray-600 font-medium">Anotador:</label>
        <select
          value={auditUser}
          onChange={(e) => { setAuditUser(e.target.value); onSelect(e.target.value); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
        >
          <option value="">— Seleccionar —</option>
          {annotators.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {data && !loading && auditUser && (
          <span className="text-sm text-gray-500">
            Total histórico: <strong className="text-gray-900">{data.total}</strong> oraciones
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-6">
        Histórico completo del anotador en todos los batches y tipos (leído del dataset compartido por sesión).
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm">{error}</div>
      )}

      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse h-32 shadow-sm" />
      )}

      {!loading && !auditUser && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
          <div className="text-4xl mb-3">🧑‍💻</div>
          <p className="text-gray-500 text-sm">Seleccioná un anotador para ver su histórico completo.</p>
        </div>
      )}

      {data && !loading && auditUser && Object.keys(data.by_type).length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
          <p className="text-gray-500 text-sm">{auditUser} no tiene anotaciones registradas.</p>
        </div>
      )}

      {data && !loading && auditUser && Object.keys(data.by_type).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {Object.entries(data.by_type).map(([atype, info]) => {
            const meta = TYPE_META[atype] || { label: atype, color: '#64748b', barColor: 'bg-gray-400' };
            const batches = Object.entries(info.batches).sort((a, b) => Number(a[0]) - Number(b[0]));
            return (
              <div key={atype} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold uppercase tracking-wide text-sm" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{info.total} oraciones</span>
                </div>
                <div className="space-y-1">
                  {batches.map(([bnum, cnt]) => {
                    const complete = cnt >= quota;
                    const pct = Math.min(100, Math.round((cnt / quota) * 100));
                    return (
                      <div key={bnum} className="flex items-center gap-3 py-1">
                        <span className="w-4 text-emerald-600 text-sm font-bold">{complete ? '✓' : ''}</span>
                        <span className="w-20 text-sm text-gray-700">Batch {String(bnum).padStart(2, '0')}</span>
                        <div className="flex-1">
                          <ProgressBar pct={pct} barColor={complete ? 'bg-emerald-500' : meta.barColor} />
                        </div>
                        <span className="text-xs text-gray-500 w-14 text-right">{cnt}/{quota}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ReportPage;
